import { useQuery } from "@tanstack/react-query";
import Sidebar from "@/components/sidebar";
import { apiRequest } from "@/lib/queryClient";
import { usePermissions } from "@/hooks/use-permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  FileText, 
  Download, 
  TrendingUp, 
  Calendar,
  Building2,
  AlertTriangle,
  CheckCircle,
  Clock
} from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '@/utils/date-formatter';
import { useAlert } from '@/hooks/use-alert';
import { useProjectStatusDisplay } from '@/hooks/use-project-statuses';
import type { Project, Ticket } from "@mangelapp/shared";

interface Stats {
  activeProjects: number;
  openTickets: number;
  inProgressTickets: number;
  completedTickets: number;
}

export default function Reports() {
  const { t } = useTranslation(['pages.reports', 'components.ticket']);
  const { alert } = useAlert();
  const { formatDateTime } = useDateFormat();
  
  // Check permissions using the new organization-based system
  const { hasPermission } = usePermissions();
  const canAccessReports = hasPermission('manage_organization') || hasPermission('view_reports');
  
  // Check if user has permission to access reports
  if (!canAccessReports) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900">{t('pages.reports:accessDenied.title')}</h1>
          <p className="text-gray-600 mt-2">{t('pages.reports:accessDenied.message')}</p>
        </div>
      </div>
    );
  }
  
  const { data: projectsResponse, isLoading: projectsLoading } = useQuery({
    queryKey: ["/api/projects"],
    queryFn: async () => {
      const response = await apiRequest("/api/projects");
      return await response.json();
    },
  });

  const projects = projectsResponse?.data || [];

  const { data: ticketsResponse, isLoading: ticketsLoading } = useQuery({
    queryKey: ["/api/tickets"],
    queryFn: async () => {
      const response = await apiRequest("/api/tickets");
      return await response.json();
    },
  });

  const tickets = ticketsResponse?.data || [];

  const { data: statsResponse } = useQuery({
    queryKey: ["/api/stats"],
    queryFn: async () => {
      const response = await apiRequest("/api/stats");
      return await response.json();
    },
  });

  const stats = statsResponse?.data;

  if (projectsLoading || ticketsLoading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-lg">{t('pages.reports:loading')}</div>
        </div>
      </div>
    );
  }

  // Calculate project statistics
  const projectStats = {
    total: projects.length,
    completed: projects.filter(p => p.status === 'completed').length,
    delayed: projects.filter(p => p.status === 'delayed').length,
    onTrack: projects.filter(p => p.status === 'in_construction').length,
    avgProgress: projects.length > 0 
      ? Math.round(projects.reduce((sum, p) => sum + (p.fortschritt || 0), 0) / projects.length)
      : 0
  };

  // Calculate ticket statistics
  const ticketStats = {
    total: tickets.length,
    open: tickets.filter(t => (t as any).statusSemanticType === 'open').length,
    inProgress: tickets.filter(t => (t as any).statusSemanticType === 'in_progress').length,
    completed: tickets.filter(t => (t as any).statusSemanticType === 'completed').length,
    highPriority: tickets.filter(t => t.priority === 'high' || t.priority === 'critical').length
  };

  // Get recent activity
  const recentActivity = [
    ...projects.map(p => ({
      type: 'project' as const,
      title: `${t('pages.reports:activity.project')}: ${p.name}`,
      date: p.erstelltAm || new Date(),
      status: p.status,
      priority: p.prioritaet
    })),
    ...tickets.map(t => ({
      type: 'ticket' as const,
      title: `${t('pages.reports:activity.defect')}: ${t.title}`,
      date: t.createdAt || new Date(),
      status: t.status,
      priority: t.priority
    }))
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 10);

  const handleExportReport = async (type: string) => {
    // In a real app, this would generate and download a report
    await alert(t('pages.reports:export.message', { type }));
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden ml-12 sm:ml-14 md:ml-16 lg:ml-14 xl:ml-16 2xl:ml-20">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h1 className="text-lg font-bold text-slate-900 uppercase tracking-wide">{t('pages.reports:title')}</h1>
              <Button variant="outline" className="bg-green-100 text-green-800 text-xs font-medium px-3 h-7 border-green-200">
                {format(new Date(), "MMMM yyyy", { locale: de })}
              </Button>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => handleExportReport(t('pages.reports:export.complete'))} className="h-7 px-2.5 text-xs font-medium">
                <Download className="h-3 w-3 mr-1" />
                {t('pages.reports:export.button')}
              </Button>
              
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-medium">MK</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto px-3 pt-3 pb-0 -mr-3 bg-slate-50">
          
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-xs font-medium">{t('pages.reports:stats.totalProjects')}</p>
                    <p className="text-lg font-bold text-slate-900 mt-1">
                      {projectStats.total}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-600">
                  <div>{t('pages.reports:stats.completed')}: {projectStats.completed}</div>
                  <div>{t('pages.reports:stats.delayed')}: {projectStats.delayed}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-xs font-medium">{t('pages.reports:stats.avgProgress')}</p>
                    <p className="text-lg font-bold text-slate-900 mt-1">
                      {projectStats.avgProgress}%
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                </div>
                <div className="mt-3">
                  <Progress value={projectStats.avgProgress} className="h-1.5" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-xs font-medium">{t('pages.reports:stats.openDefects')}</p>
                    <p className="text-lg font-bold text-slate-900 mt-1">
                      {ticketStats.open}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-600">
                  <div>{t('pages.reports:stats.highPriority')}: {ticketStats.highPriority}</div>
                  <div>{t('pages.reports:stats.inProgress')}: {ticketStats.inProgress}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-xs font-medium">{t('pages.reports:stats.completedDefects')}</p>
                    <p className="text-lg font-bold text-slate-900 mt-1">
                      {ticketStats.completed}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
                <div className="mt-3 text-xs text-green-600">
                  {ticketStats.total > 0 
                    ? `${Math.round((ticketStats.completed / ticketStats.total) * 100)}% ${t('pages.reports:stats.completedPercent')}`
                    : `0% ${t('pages.reports:stats.completedPercent')}`
                  }
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            
            {/* Project Report */}
            <Card>
              <CardHeader className="p-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-sm">
                    <Building2 className="h-4 w-4 mr-2" />
                    {t('pages.reports:sections.projectOverview')}
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    onClick={() => handleExportReport(t('pages.reports:export.project'))}
                    className="h-7 px-2.5 text-xs"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    {t('pages.reports:export.button')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {projects.slice(0, 5).map((project) => {
                    const { displayName: statusDisplayName, color: statusColor } = useProjectStatusDisplay((project as any).projectStatusId);
                    return (
                      <div key={project.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-900 truncate text-xs">
                            {project.name}
                          </h4>
                          <p className="text-xs text-slate-600">
                            {project.managerName}
                          </p>
                          <div className="mt-2">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                              <span>{t('pages.reports:project.progress')}</span>
                              <span>{project.fortschritt}%</span>
                            </div>
                            <Progress value={project.fortschritt} className="h-1" />
                          </div>
                        </div>
                        <div className="ml-3 text-right">
                          <Badge 
                            variant="secondary"
                            className="text-xs px-2 py-0.5"
                            style={{
                              backgroundColor: statusColor || '#e5e7eb',
                              color: '#1f2937',
                            }}
                          >
                            {statusDisplayName || t('pages.reports:status.unknown')}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader className="p-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-sm">
                    <Clock className="h-4 w-4 mr-2" />
                    {t('pages.reports:sections.recentActivity')}
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    onClick={() => handleExportReport(t('pages.reports:export.activity'))}
                    className="h-7 px-2.5 text-xs"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    {t('pages.reports:export.button')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2.5">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-center space-x-2.5 p-2 hover:bg-slate-50 rounded-lg">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        activity.type === 'project' ? 'bg-blue-100' : 'bg-red-100'
                      }`}>
                        {activity.type === 'project' ? (
                          <Building2 className="h-3 w-3 text-blue-600" />
                        ) : (
                          <AlertTriangle className="h-3 w-3 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-900 truncate">
                          {activity.title}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatDateTime(activity.date)}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs px-1.5 py-0.5">
                        {activity.priority || '-'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Export Options */}
          <Card className="mt-4">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center text-sm">
                <FileText className="h-4 w-4 mr-2" />
                {t('pages.reports:sections.reportExports')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Button 
                  variant="outline" 
                  className="flex items-center justify-center p-4 h-auto flex-col space-y-1.5"
                  onClick={() => handleExportReport(t('pages.reports:exports.projectSummary'))}
                >
                  <Building2 className="h-6 w-6 text-blue-600" />
                  <span className="font-medium text-xs">{t('pages.reports:exports.projectSummary')}</span>
                  <span className="text-xs text-slate-600">{t('pages.reports:exports.projectSummaryDesc')}</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="flex items-center justify-center p-4 h-auto flex-col space-y-1.5"
                  onClick={() => handleExportReport(t('pages.reports:exports.defectReport'))}
                >
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                  <span className="font-medium text-xs">{t('pages.reports:exports.defectReport')}</span>
                  <span className="text-xs text-slate-600">{t('pages.reports:exports.defectReportDesc')}</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="flex items-center justify-center p-4 h-auto flex-col space-y-1.5"
                  onClick={() => handleExportReport(t('pages.reports:exports.monthlyReport'))}
                >
                  <Calendar className="h-6 w-6 text-green-600" />
                  <span className="font-medium text-xs">{t('pages.reports:exports.monthlyReport')}</span>
                  <span className="text-xs text-slate-600">{t('pages.reports:exports.monthlyReportDesc')}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}
